/* eslint-disable no-restricted-imports */
import {
  Environment,
  HostConfig,
  Locale,
  StaticHostConfig,
} from '@aboutyou/config';
import type {GrpcConfig} from '@aboutyou/data-fetcher';
// eslint-disable-next-line monorepo/no-internal-import
import {useStandaloneGrpcRequest} from '@aboutyou/data-fetcher/src/grpc/useStandaloneGrpcRequest'; // this avoids a circular dependency within Jest
import {TranslationService_DownloadLazy} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/translation/v1/service.grpc.lazy';
import {Log} from '@aboutyou/mlm';
// eslint-disable-next-line monorepo/no-internal-import
import {ServerInMemoryCache} from '@aboutyou/storage/src/nodeInMemoryCache/ServerInMemoryCache';
import {Translations} from './I18nContext';

const THIRTY_MINUTES = 30 * 60 * 1000;
const FAILURE_TTL = 5 * 1000;

const translationCache = new ServerInMemoryCache<
  Promise<Translations | undefined>
>({
  ttl: THIRTY_MINUTES,
});

export async function generateTranslations({
  hostConfig,
  grpcConfig,
}: {
  hostConfig: StaticHostConfig;
  grpcConfig: GrpcConfig;
}) {
  const cached = translationCache.get(hostConfig.hostname);
  if (cached) {
    return cached;
  }

  // Store the in-flight Promise (not the resolved value) so concurrent
  // callers share a single upstream request. On failure, overwrite with a
  // short-TTL resolved-undefined entry so a sustained outage doesn't
  // unleash a retry storm — every caller in the next FAILURE_TTL window
  // gets the English fallback without hitting the backend.
  const inflight: Promise<Translations | undefined> = getTranslationsFromServer(
    hostConfig,
    grpcConfig,
  ).catch(e => {
    translationCache.set(hostConfig.hostname, Promise.resolve(undefined), {
      ttl: FAILURE_TTL,
    });
    const message = `Could not fetch translations from the server, falling back to english. Requested locale: ${hostConfig.locale}. Error: ${e}`;
    if (process.env.NODE_ENV === 'development') {
      throw new Error(message);
    }
    Log.error('@aboutyou/bootstrap', message);
    return undefined;
  });

  translationCache.set(hostConfig.hostname, inflight);
  return inflight;
}

async function getTranslationsFromServer(
  hostConfig: HostConfig<Locale, Environment>,
  grpcConfig: GrpcConfig,
): Promise<Translations> {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const translationsRequest = useStandaloneGrpcRequest(
    {
      method: TranslationService_DownloadLazy,
      params: {contextKeys: []},
    },
    {
      hostConfig: {
        hostname: hostConfig.tadaridaUrl,
        legacyHostname: hostConfig.gripsUrl,
      },
      grpcConfig,
    },
  );
  const data = await translationsRequest();
  const translations: Translations = {};
  for (const entry of data.translations) {
    translations[entry.contextKey] = entry.text;
  }
  return translations;
}
